import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from '~/assets/icons/HelpIcon';

export type DataType = 'text' | 'number' | 'boolean' | 'date' | 'closelist' | 'openlist';

export interface ProfileAttribute {
  id?: string;
  _id?: string;
  name: string;
  dataType: DataType;
  description?: string;
  useAI: boolean;
  closelist?: {
    options: string[];
    multiple: boolean;
  };
}

interface Props {
  isOpen: boolean;
  isEdit?: boolean;
  initialValue?: ProfileAttribute | null;
  onClose: () => void;
  onSubmit: (value: ProfileAttribute) => void;
}

export function ProfileAttributeDialog({ isOpen, isEdit = false, initialValue, onClose, onSubmit }: Props) {
  const [t] = useTranslation('teams');
  const [form, setForm] = useState<ProfileAttribute>({
    name: '',
    dataType: 'text',
    description: '',
    useAI: true,
    closelist: {
      options: [],
      multiple: false,
    },
  });
  const [newOption, setNewOption] = useState('');
  const [showDataTypesHelp, setShowDataTypesHelp] = useState(false);

  useEffect(() => {
    if (initialValue) {
      setForm(initialValue);
    } else {
      // Reset form to default values when creating a new attribute
      setForm({
        name: '',
        dataType: 'text',
        description: '',
        useAI: true,
        closelist: {
          options: [],
          multiple: false,
        },
      });
    }
    // Reset other states when dialog opens
    setNewOption('');
    setShowDataTypesHelp(false);
  }, [initialValue, isOpen]);

  if (!isOpen) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    
    const submitData = { ...form };
    
    // Only include closelist data if dataType is 'closelist'
    if (form.dataType !== 'closelist') {
      delete submitData.closelist;
    }
    
    onSubmit(submitData);
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-xl">
        <h3 className="text-lg font-bold text-brand-primary">
          {isEdit ? t('edit_profile_variable') : t('add_profile_variable')}
        </h3>
        <form className="mt-4 space-y-6" onSubmit={submit}>
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text font-medium">{t('label_name')}</span>
            </label>
            <input
              className="input input-bordered input-brand w-full"
              placeholder={t('placeholder_variable_name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text font-medium flex items-center gap-2">
                {t('field_data_type')}
                <button
                  type="button"
                  className="btn btn-ghost btn-xs p-1 hover:bg-base-200"
                  onClick={() => setShowDataTypesHelp(true)}
                  title="Ver información sobre tipos de datos"
                >
                  <HelpIcon size={14} className="text-base-content/60" />
                </button>
              </span>
            </label>
            <select
              className="select select-bordered w-full"
              value={form.dataType}
              onChange={(e) => setForm({ ...form, dataType: e.target.value as DataType })}
            >
              <option value="text">{t('data_type_text')}</option>
              <option value="number">{t('data_type_number')}</option>
              <option value="boolean">{t('data_type_boolean')}</option>
              <option value="date">{t('data_type_date')}</option>
              <option value="openlist">{t('data_type_openlist')}</option>
              <option value="closelist">{t('data_type_closelist')}</option>
            </select>
          </div>

          <div className="form-control w-full">
            <label className="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={form.useAI}
                onChange={(e) => setForm({ ...form, useAI: e.target.checked })}
              />
              <span className="label-text font-medium">{t('field_use_ai')}</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              * {t('field_use_ai_description')}
            </p>
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text font-medium">{t('field_description')}</span>
            </label>
            <textarea
              className="textarea textarea-bordered min-h-[100px] w-full"
              placeholder={t('placeholder_description_field')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          {form.dataType === 'closelist' && (
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">{t('closelist_options')}</span>
              </label>
              
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input input-bordered flex-1"
                    placeholder={t('add_option_placeholder')}
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newOption.trim() && !form.closelist?.options.includes(newOption.trim())) {
                          setForm({
                            ...form,
                            closelist: {
                              ...form.closelist!,
                              options: [...(form.closelist?.options || []), newOption.trim()]
                            }
                          });
                          setNewOption('');
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (newOption.trim() && !form.closelist?.options.includes(newOption.trim())) {
                        setForm({
                          ...form,
                          closelist: {
                            ...form.closelist!,
                            options: [...(form.closelist?.options || []), newOption.trim()]
                          }
                        });
                        setNewOption('');
                      }
                    }}
                  >
                    {t('add')}
                  </button>
                </div>

                {form.closelist?.options && form.closelist.options.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">{t('current_options')}:</div>
                    <div className="flex flex-wrap gap-2">
                      {form.closelist.options.map((option, index) => (
                        <div key={index} className="badge badge-outline gap-2">
                          {option}
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            onClick={() => {
                              setForm({
                                ...form,
                                closelist: {
                                  ...form.closelist!,
                                  options: form.closelist!.options.filter((_, i) => i !== index)
                                }
                              });
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-control">
                  <label className="label cursor-pointer justify-start gap-3">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={form.closelist?.multiple || false}
                      onChange={(e) => setForm({
                        ...form,
                        closelist: {
                          ...form.closelist!,
                          multiple: e.target.checked
                        }
                      })}
                    />
                    <span className="label-text">{t('allow_multiple_selection')}</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="submit" className="btn btn-primary rounded-full">
              {isEdit ? t('update_variable') : t('create_variable')}
            </button>
          </div>
        </form>
      </div>
      <div className="modal-backdrop" onClick={onClose} />

      {/* Data Types Help Modal */}
      {showDataTypesHelp && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="text-lg font-bold text-brand-primary mb-4">
              {t('data_types_help_title')}
            </h3>
            <div className="space-y-4">
              <div className="prose prose-sm max-w-none">
                <div className="space-y-3">
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.text') }} />
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.number') }} />
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.boolean') }} />
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.date') }} />
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.closelist') }} />
                  </div>
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('data_types_help_content.openlist') }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-action">
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => setShowDataTypesHelp(false)}
              >
                {t('close')}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowDataTypesHelp(false)} />
        </div>
      )}
    </div>
  );
}
